import { useEffect } from 'react';

interface UseSwipeGestureOptions {
  /** Swipe irány (csak 'right' támogatott egyelőre) */
  direction: 'right';
  /** Bal széltől maximális érintési kezdőpont (px) */
  edgeThreshold?: number;
  /** Minimális swipe távolság (px) */
  minDistance?: number;
  /** Callback swipe detektáláskor */
  onSwipe: () => void;
  /** Ha true, a hook nem aktiválódik (pl. wizard módban) */
  disabled?: boolean;
}

/**
 * useSwipeGesture — Bal szélről jobbra swipe detektálás
 * Csak touchstart.clientX < edgeThreshold esetén aktiválódik.
 * Nincs globális CSS touch-action módosítás — horizontális scroll sértetlen marad.
 *
 * @see tech-spec-nav-ux-overhaul.md F3-T7
 */
export function useSwipeGesture({
  direction,
  edgeThreshold = 20,
  minDistance = 50,
  onSwipe,
  disabled = false,
}: UseSwipeGestureOptions) {
  useEffect(() => {
    if (disabled || typeof window === 'undefined') return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      // F6 fix: ne aktiválódjon form inputon, scrollable container-en belüli touch esetén
      const target = e.target as Element | null;
      if (target) {
        const tag = target.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        if (target.closest('[data-no-swipe]')) return;
      }
      startX = touch.clientX;
      startY = touch.clientY;
      // Csak a bal szélről indított swipe-ot trackelünk
      tracking = startX < edgeThreshold;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      const touch = e.changedTouches[0];
      if (!touch) return;
      const dx = touch.clientX - startX;
      const dy = Math.abs(touch.clientY - startY);

      // Jobbra swipe, nem inkább vertikális scroll
      if (direction === 'right' && dx > minDistance && dy < dx) {
        onSwipe();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!tracking) return;
      const touch = e.touches[0];
      if (!touch) return;
      const dx = touch.clientX - startX;
      const dy = Math.abs(touch.clientY - startY);
      // Ha inkább vertikális → abbahagyjuk
      if (dy > dx) tracking = false;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [direction, edgeThreshold, minDistance, onSwipe, disabled]);
}
